import styles from './_stylesParams.module.scss'
// @ts-ignore;
import LexendDecsFonts from '../assets/fonts/LexendDeca-VariableFont_wght.ttf'
import {
    createTheme,
    PaletteColor,
    PaletteColorOptions,
    SimplePaletteColorOptions,
} from '@mui/material/styles'
import pxToRem from '../helpers/pxToRem'

declare module '@mui/material/styles' {
    interface Theme {
        layout: {
            appBarHeight: string
            viewportBelowAppBar: string
        }
    }

    interface ThemeOptions {
        layout?: {
            appBarHeight?: string
            viewportBelowAppBar?: string
        }
    }

    interface TypographyVariants {
        pl: React.CSSProperties
        pm: React.CSSProperties
        ps: React.CSSProperties
        pxs: React.CSSProperties
    }

    // allow configuration using `createTheme()`
    interface TypographyVariantsOptions {
        pl?: React.CSSProperties
        pm?: React.CSSProperties
        ps?: React.CSSProperties
        pxs?: React.CSSProperties
    }

    interface Palette {
        neutrals: Record<number, string>
        shades: Record<number, string>
        primaryYellow500: PaletteColor
        primaryPurple: PaletteColor
        primaryGreen500: PaletteColor
        primaryBlue500: PaletteColor
        destructiveRed500: PaletteColor
        neutrals500: PaletteColor
        shades100: PaletteColor
        secondaryBlue900: PaletteColor
    }
    interface PaletteOptions {
        neutrals: Record<number, string>
        shades: Record<number, string>
        primaryYellow500: SimplePaletteColorOptions
        primaryBlue500: PaletteColorOptions
        primaryPurple: PaletteColorOptions
        primaryGreen500: PaletteColorOptions
        destructiveRed500: PaletteColorOptions
        neutrals500: PaletteColorOptions
        shades100: PaletteColorOptions
        secondaryBlue900: PaletteColorOptions
    }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        pl: true
        pm: true
        ps: true
        pxs: true
    }
}
declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        primaryYellow500: true
        primaryBlue500: true
        primaryPurple: true
        primaryGreen500: true
        destructiveRed500: true
        neutrals500: true
        shades100: true
        secondaryBlue900: true
    }
}
declare module '@mui/material/Paper' {
    interface PaperPropsVariantOverrides {
        review: true
        accountPresubmission: true
        accountDefault: true
        accountIncomplete: true
        accountRejected: true
    }
}

const { palette } = createTheme()
const { augmentColor } = palette
const createColor = (mainColor: any) =>
    augmentColor({ color: { main: mainColor } })
const denseAppBarHeight = pxToRem(48)

export const theme = createTheme({
    layout: {
        appBarHeight: denseAppBarHeight,
        viewportBelowAppBar: `calc(100dvh - ${denseAppBarHeight})`,
    },
    typography: {
        fontFamily: 'Lexend Deca',
        button: {
            textTransform: 'none',
        },

        h1: {
            fontSize: pxToRem(40),
            fontStyle: 'normal',
            lineHeight: pxToRem(48),
            letterSpacing: '-2%',
        },
        h2: {
            fontSize: pxToRem(36),
            fontStyle: 'normal',
            lineHeight: pxToRem(44) /* 142.857% */,
            letterSpacing: '-2%',
        },
        h3: {
            fontSize: pxToRem(32),
            fontStyle: 'normal',
            lineHeight: pxToRem(40) /* 142.857% */,
            letterSpacing: '-2%',
        },
        h4: {
            fontSize: pxToRem(28),
            fontStyle: 'normal',
            lineHeight: pxToRem(36) /* 142.857% */,
            letterSpacing: '-2%',
        },
        h5: {
            fontSize: pxToRem(24),
            fontStyle: 'normal',
            lineHeight: pxToRem(32) /* 142.857% */,
            letterSpacing: '-2%',
        },
        h6: {
            fontSize: pxToRem(20),
            fontStyle: 'normal',
            lineHeight: pxToRem(28) /* 142.857% */,
            letterSpacing: '-2%',
        },
        pl: {
            fontSize: pxToRem(18),
            fontStyle: 'normal',
            lineHeight: pxToRem(28) /* 142.857% */,
            letterSpacing: '-2%',
        },
        pm: {
            fontSize: pxToRem(16),
            fontStyle: 'normal',
            lineHeight: pxToRem(24) /* 142.857% */,
            letterSpacing: '-2%',
        },
        ps: {
            fontSize: pxToRem(14),
            fontStyle: 'normal',
            lineHeight: pxToRem(20) /* 142.857% */,
            letterSpacing: pxToRem(-0.28),
        },
        pxs: {
            fontSize: pxToRem(12),
            fontStyle: 'normal',
            lineHeight: pxToRem(20) /* 142.857% */,
            letterSpacing: pxToRem(-0.24),
        },
    },
    palette: {
        // action: {
        //     disabled: styles.neutrals100,
        // },
        neutrals: {
            50: styles.neutrals50,
            100: styles.neutrals100,
            200: styles.neutrals200,
            300: styles.neutrals300,
            400: styles.neutrals400,
            500: styles.neutrals500,
            600: styles.neutrals600,
            700: styles.neutrals700,
            800: styles.neutrals800,
        },
        shades: {
            0: styles.shades0,
            100: styles.shades100,
        },
        text: {
            primary: styles.shades100,
            disabled: styles.neutrals500,
        },
        success: {
            main: styles.primaryGreen500,
            contrastText: '#fff',
        },

        primaryGreen500: {
            main: styles.primaryGreen500,
            // Hover color
            dark: styles.primaryGreen400,
            light: styles.secondaryGreen50,
            contrastText: styles.shades0,
        },
        primaryBlue500: {
            main: styles.primaryBlue500,
            // Hover color
            dark: styles.secondaryBlue300,
            light: styles.primaryBlue300,
            contrastText: styles.shades0,
        },
        secondaryBlue900: {
            main: styles.secondaryBlue900,
            // Hover color
            dark: styles.secondaryBlue800,
            light: styles.primaryBlue300,
            contrastText: styles.shades0,
        },
        destructiveRed500: {
            main: styles.destructiveRed500,
            // Hover color
            dark: '#D4524A',
            light: styles.destructiveRed400,
            contrastText: styles.shades0,
        },
        primaryYellow500: {
            main: styles.primaryYellow500,
            // Hover color
            dark: styles.primaryYellow200,
            light: styles.primaryYellow50,
            contrastText: styles.shades0,
        },
        neutrals500: {
            main: styles.neutrals500,
            // Hover color
            dark: styles.neutrals400,
            light: styles.neutrals400,
            contrastText: styles.shades100,
        },
        shades100: {
            main: styles.shades100,
            // Hover color
            dark: styles.shades100,
            light: styles.neutrals50,
            contrastText: styles.shades0,
        },
        primaryPurple: createColor('#803B79'),
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: `
                @font-face {
                    font-family: 'Lexend Deca';
                    src: url(${LexendDecsFonts})
                }
                
                :root {
                    --app-bar-height: ${denseAppBarHeight};
                    --viewport-below-app-bar: calc(100vh - ${denseAppBarHeight});
                }
                `,
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    variants: [
                        {
                            props: { variant: 'dense' },
                            style: {
                                minHeight: denseAppBarHeight,
                                height: denseAppBarHeight,
                            },
                        },
                    ],
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: '40px',
                },
                paperFullScreen: { borderRadius: 0, scrollbarWidth: 'thin' },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: styles.neutrals100,
                },
            },
        },
        MuiTypography: {
            defaultProps: {
                variant: 'pm',
                variantMapping: {
                    pl: 'span',
                    pm: 'span',
                    ps: 'small',
                    pxs: 'small',
                },
            },
            styleOverrides: {
                root: {
                    variants: [
                        {
                            props: { variant: 'h1' },
                            style: {
                                fontSize: pxToRem(40),
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: pxToRem(48) /* 142.857% */,
                                letterSpacing: '-2%',
                            },
                        },
                        {
                            props: { variant: 'h2' },
                            style: {
                                fontSize: pxToRem(36),
                                fontStyle: 'normal',
                                lineHeight: pxToRem(44) /* 142.857% */,
                                letterSpacing: '-2%',
                            },
                        },
                        {
                            props: { variant: 'h3' },
                            style: {
                                fontSize: pxToRem(32),
                                fontStyle: 'normal',
                                lineHeight: pxToRem(40) /* 142.857% */,
                                letterSpacing: '-2%',
                            },
                        },
                        {
                            props: { variant: 'h4' },
                            style: {
                                fontSize: pxToRem(28),
                                fontStyle: 'normal',
                                lineHeight: pxToRem(36) /* 142.857% */,
                                letterSpacing: '-2%',
                            },
                        },
                        {
                            props: { variant: 'h5' },
                            style: {
                                fontSize: pxToRem(24),
                                fontStyle: 'normal',
                                lineHeight: pxToRem(32) /* 142.857% */,
                                letterSpacing: '-2%',
                            },
                        },
                        {
                            props: { variant: 'h6' },
                            style: {
                                fontSize: pxToRem(20),
                                fontStyle: 'normal',
                                lineHeight: pxToRem(28) /* 142.857% */,
                                letterSpacing: '-2%',
                            },
                        },
                    ],
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: ({ ownerState }) => {
                    const paletteColor: PaletteColor | undefined =
                        !ownerState.color || ownerState.color === 'inherit'
                            ? undefined
                            : typeof theme.palette[ownerState.color] ===
                                'object'
                              ? theme.palette[ownerState.color]
                              : theme.palette.primary
                    return {
                        boxShadow: 'none',
                        px: 16,
                        py: 14,
                        fontWeight: 400,
                        borderRadius: '16px',
                        '&:hover': {
                            boxShadow: 'none',
                            ...(ownerState.variant === 'text' && {
                                backgroundColor: 'transparent', // not 'none'
                                color: paletteColor?.dark,
                            }),
                        },
                    }
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled': {
                        color: styles.neutrals500,
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: styles.neutrals100,
                    },
                    '&.Mui-disabled': {
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: styles.neutrals100,
                            '&:hover': {
                                borderColor: styles.neutrals100,
                            },
                        },
                    },
                },
            },
        },

        MuiAccordion: {
            variants: [
                {
                    props: { variant: 'review' },
                    style: {
                        backgroundColor: styles.primaryPurple500,
                        '& .accordion-subtitle': {
                            color: `${styles.primaryPurple500} !important`,
                        },
                        '& .accordion-positive': {
                            color: `${styles.primaryPurple500} !important`,
                        },
                        // '& .accordion-iconBtn': {
                        //     color: '#fff',
                        // },
                        '& .MuiButtonBase-root': {
                            cursor: 'default !important',
                        },

                        '& .MuiAccordionSummary-expandIconWrapper svg': {
                            stroke: styles.primaryPurple500,
                        },

                        '& .MuiAccordionDetails-root .MuiTypography-root': {
                            color: `${styles.shades100}`,
                        },
                    },
                },
                {
                    props: { variant: 'accountDefault' },
                    style: {
                        opacity: 0.9,
                        backgroundColor: styles.secondaryBlue800,
                        '& .accordion-subtitle': {
                            color: `${styles.primaryYellow400} !important`,
                        },
                        '& .accordion-positive': {
                            color: `${styles.secondaryBlue800} !important`,
                        },
                        '& .accordion-iconBtn': {
                            color: '#fff',
                        },

                        '& .MuiAccordionDetails-root .MuiTypography-root': {
                            color: styles.shades100,
                        },
                    },
                },
                {
                    props: { variant: 'accountIncomplete' },
                    style: {
                        backgroundColor: styles.neutrals50,
                        '& .MuiAccordionSummary-expandIconWrapper svg': {
                            stroke: styles.shades100,
                        },
                        '& .MuiTypography-root, & p': {
                            color: styles.shades100,
                        },
                        '& .accordion-subtitle': {
                            color: `${styles.primaryYellow400} !important`,
                        },
                        '& .accordion-positive': {
                            color: `${styles.secondaryBlue800} !important`,
                        },
                        '& .accordion-iconBtn': {
                            color: styles.shades100,
                        },
                    },
                },
                {
                    props: { variant: 'accountRejected' },
                    style: {
                        backgroundColor: styles.destructiveRed500,
                        '& .accordion-iconBtn': {
                            color: '#fff',
                        },
                        '& .MuiAccordionSummary-expandIconWrapper svg': {
                            stroke: styles.shades0,
                        },
                        '& .MuiAccordionDetails-root .MuiTypography-root': {
                            color: styles.shades100,
                        },
                        '& .accordion-subtitle': {
                            color: `${styles.destructiveRed500} !important`,
                        },
                        '& .accordion-positive': {
                            color: `${styles.secondaryBlue800} !important`,
                        },
                    },
                },
            ],
            styleOverrides: {
                root: {
                    borderRadius: pxToRem(8),
                    border: 0,
                    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
                    '&::before': {
                        display: 'none',
                    },
                    '& .MuiAccordionSummary-root': {
                        minHeight: 'unset',
                    },
                    '& .MuiAccordionSummary-content': {
                        marginTop: pxToRem(16),
                        marginBottom: pxToRem(16),
                        '>.MuiStack-root': {
                            width: '100%',
                            height: '100%',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginRight: pxToRem(16),
                        },
                    },
                    '& .MuiAccordionSummary-expandIconWrapper svg': {
                        stroke: styles.shades0,
                    },
                    '& .MuiTypography-root, & p': {
                        color: styles.shades0,
                        fontSize: pxToRem(18),
                        fontWeight: 400,
                        lineHeight: pxToRem(28),
                        letterSpacing: pxToRem(-0.36),
                        fontFamily: 'Lexend Deca',
                    },
                },
            },
        },
    },
})
